import React, { useEffect,useMemo, useState } from 'react';
import { AppButton, Box, Inscription, Preloader, Text } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';
import theme from '@/styles/theme';

import { ExpressSetup, ExpressSetupWizardStepTitle, ExpressSetupWizardStepType } from '../ExpressSetup.types';
import ExpressSetupActivateModal from '../ExpressSetupActivateModal';
import FAQModal from '../ExpressSetupFAQ/FAQModal';
import { usePlansStore } from '../ExpressSetupWizard/PlansWizard/Plans/stores';
import { SAVE_ERROR_TEXT } from '../ExpressSetupWizard/PlansWizard/PlansWizard'; 
import { useActivationAllowed, useHandleExpressSetupSetUp } from '../hooks';

import ProgressBarActivationContent from './ProgressBarActivationContent';
import ProgressBarItem from './ProgressBarItem'; 

interface Props {
  expressSetupData?: ExpressSetup;
  isBannerMode?: boolean;
  stepName?: ExpressSetupWizardStepType;
  isDisabled?: boolean;
  isLoading?: boolean;
  isActivateMode?: boolean;
  isSticky?: boolean;
  isAbandoned?: boolean;
}

const ProgressBar: React.FC<Props> = ({
  expressSetupData,
  isBannerMode,
  stepName,
  isDisabled,
  isLoading,
  isActivateMode,
  isSticky,
  isAbandoned,
}) => {
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [isVisibleExpressSetupPopup, setVisibleActivateExpressSetupPopup] = useState(false);
  const [isFAQModalVisible, handleOpenFAQModal] = useState(false);
  const plansState = usePlansStore((state) => state.state);
  const [isSaveErrorModalVisible, setSaveErrorModalVisible] = useState<boolean>(false);
  const isActivationAllowed = useActivationAllowed(expressSetupData);
  const { onSavePlansHandler, isPlansLoading, isPlansServerError } = useHandleExpressSetupSetUp();
  const progressBarOptions = useMemo(() => [
    { title: ExpressSetupWizardStepTitle[ExpressSetupWizardStepType.MainContact], isEntityExist: !!expressSetupData?.mainContact, url: `#${ExpressSetupWizardStepType.MainContact}`, isActive: stepName === ExpressSetupWizardStepType.MainContact },
    { title: ExpressSetupWizardStepTitle[ExpressSetupWizardStepType.BankAccount], isEntityExist: !!expressSetupData?.accountNumber, url: `#${ExpressSetupWizardStepType.BankAccount}`, isActive: stepName === ExpressSetupWizardStepType.BankAccount },
    { title: ExpressSetupWizardStepTitle[ExpressSetupWizardStepType.Plans], isEntityExist: !!expressSetupData?.planEndDate && !!expressSetupData?.plans?.length, isLastItem: true, url: `#${ExpressSetupWizardStepType.Plans}`, isActive: stepName === ExpressSetupWizardStepType.Plans },
  ], [stepName, expressSetupData]);

  const progressBarStyles: React.CSSProperties = useMemo(() => {
    return isSticky
      ? { position: 'sticky', top: isAbandoned ? '152px' : '96px', zIndex: 1000 }
      : {};
  }, [isSticky, isAbandoned]);

  useEffect(() => {
    if (isPlansServerError) setErrorModal(true);
  }, [isPlansServerError]);

  if (isActivateMode) {
    return (
      <ProgressBarActivationContent
        orgName={expressSetupData?.name}
        orgPath={expressSetupData?.orgPath}
        orgId={expressSetupData?.orgId}
      />
    );
  }

  return (
    <Box
      width="304px"
      round="container1Round"
      style={{ ...progressBarStyles, overflow: 'hidden' }}
      data-testid="express-setup-progress-bar"
      pad={{ horizontal: 's', bottom: 's', top: 'm' }}
      margin={{ bottom: 's', left: isBannerMode ? 'l' : 'none' }}
      background={isBannerMode ? 'border1' : 'module'}
    >
      <Box direction="row" justify="between" pad={{ horizontal: 's' }}>
        <Text size="16px" weight={700} style={{ lineHeight: '22px' }} color="textTitle">Progress</Text>
        <Inscription size="16px" cursor="pointer" lineHeight="22px" color="textSecondary" onClick={() => handleOpenFAQModal(true)}>Need Help?</Inscription>
      </Box>
      <Box
        width="100%"
        round="container1Round"
        background="canvas"
        pad={{ horizontal: 's', vertical: 'm' }}
        margin={{ vertical: 's' }}
      >
        {progressBarOptions.map((item, index) => (
          <ProgressBarItem
            key={index}
            title={item.title}
            isLastItem={item.isLastItem}
            isEntityExist={item.isEntityExist}
            expressSetupData={expressSetupData}
            url={item.url}
            isActive={item.isActive}
            isDisabled={isDisabled}
          />
        ))}
      </Box>
      {
        !!plansState?.plans?.length && stepName === ExpressSetupWizardStepType.Plans && (
          <AppButton
            onClick={() => onSavePlansHandler(() => setSaveErrorModalVisible(true))}
            testId="save-plans-form"
            disabled={isPlansLoading || expressSetupData?.isAbandoned || isDisabled}
            buttonType="secondary"
            style={{ width: '280px', height: '54px', marginBottom: theme.spacings.s }}
          >
            {isPlansLoading ? <Preloader color="white" /> : 'Save'}
          </AppButton>
        )
      }
      <AppButton
        onClick={() => !isLoading ? setVisibleActivateExpressSetupPopup(true) : undefined}
        width="280px"
        disabled={!isActivationAllowed || expressSetupData?.isAbandoned || isDisabled}
        style={{ height: '54px' }}
      >
        {isLoading ? <Preloader color="primary" /> : 'Activate Organization'}
      </AppButton>
      <ExpressSetupActivateModal
        isVisible={isVisibleExpressSetupPopup}
        expressSetup={expressSetupData}
        onSetVisible={setVisibleActivateExpressSetupPopup}
      />
      <WarnModal
        visible={isSaveErrorModalVisible}
        onSetVisible={setSaveErrorModalVisible}
        header={SAVE_ERROR_TEXT}
        buttonText="Got It"
      />
      <WarnModal
        testId="plans-plan_year-wizzard-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot update the Plans with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={() => onSavePlansHandler()}
        submitButtonText="Try again"
      />
      <FAQModal
        numberOfDaysBeforePlanStart={expressSetupData?.numberOfDaysBeforePlanStart}
        visible={isFAQModalVisible}
        onToggleVision={handleOpenFAQModal}
      />
    </Box>
  );
};

export default ProgressBar;
